import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { push } from "connected-react-router";
import { speak } from "@wordpress/a11y";
import _debounce from "lodash/debounce";
import classNames from "classnames";

import { getViewableSites } from "shared-frontend/redux/selectors/entities/sites";
import { retrieveSites } from "shared-frontend/redux/actions/sites";

import { linkSiteModalOpen } from "../../redux/actions/sites";
import { Alert, Button, Card, Link, Pagination, Table, Title } from "@yoast/ui-library";
import { PlusSmallIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Search from "../Search/Search";
import AddSiteModal from "../modal/AddSiteModal";
import AddSite from "../AddSite/AddSite";
import { formatDayMonthYear, getSiteSubscriptionInfo } from "../../functions/siteSubscriptionInfo";
import useSubscriptionSummary from "../../hooks/useSubscriptionSummary";
import * as styles from "./SitesOverviewCardStyles.module.scss";

const PAGE_SIZE = 5;

// How long the unused-subscriptions alert stays dismissed after the user closes it.
const ALERT_DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;
const ALERT_DISMISS_STORAGE_KEY = "sitesOverviewAlertDismissedUntil";

/**
 * Checks whether the unused-subscriptions alert is currently dismissed.
 *
 * @returns {boolean} True when a non-expired dismissal is stored.
 */
const isAlertDismissed = () => {
	try {
		const dismissedUntil = window.localStorage.getItem( ALERT_DISMISS_STORAGE_KEY );
		return dismissedUntil !== null && Number( dismissedUntil ) > Date.now();
	} catch ( e ) {
		return false;
	}
};

/**
 * Persists a dismissal of the unused-subscriptions alert for a fixed duration.
 *
 * @returns {void}
 */
const dismissAlert = () => {
	try {
		window.localStorage.setItem( ALERT_DISMISS_STORAGE_KEY, String( Date.now() + ALERT_DISMISS_DURATION ) );
	} catch ( e ) {
		// Ignore storage failures (e.g. private mode); the alert simply reappears on reload.
	}
};

const messages = defineMessages( {
	cardTitle: {
		id: "home.sitesOverview.title",
		defaultMessage: "Sites overview",
	},
	unusedActivations: {
		id: "home.sitesOverview.unusedActivations",
		defaultMessage: "You can activate your subscription(s) on {count, plural, one {# more site} other {# more sites}}, {link}.",
	},
	addASiteLink: {
		id: "home.sitesOverview.addASiteLink",
		defaultMessage: "add a site",
	},
	dismissAlert: {
		id: "home.sitesOverview.dismissAlert",
		defaultMessage: "Dismiss",
	},
	searchLabel: {
		id: "home.sitesOverview.searchLabel",
		defaultMessage: "Search sites",
	},
	headerSiteUrl: {
		id: "home.sitesOverview.headerSiteUrl",
		defaultMessage: "Site URL",
	},
	headerStatus: {
		id: "home.sitesOverview.headerStatus",
		defaultMessage: "Subscription status",
	},
	headerEndsOn: {
		id: "home.sitesOverview.headerEndsOn",
		defaultMessage: "Subscription ends on",
	},
	headerActions: {
		id: "home.sitesOverview.headerActions",
		defaultMessage: "Actions",
	},
	statusActive: {
		id: "home.sitesOverview.statusActive",
		defaultMessage: "Active",
	},
	statusInactive: {
		id: "home.sitesOverview.statusInactive",
		defaultMessage: "Inactive",
	},
	manage: {
		id: "home.sitesOverview.manage",
		defaultMessage: "Manage",
	},
	fix: {
		id: "home.sitesOverview.fix",
		defaultMessage: "Fix",
	},
	showingResults: {
		id: "home.sitesOverview.showingResults",
		defaultMessage: "Showing {from} to {to} of {total} results",
	},
	previousPage: {
		id: "home.sitesOverview.previousPage",
		defaultMessage: "Previous page",
	},
	nextPage: {
		id: "home.sitesOverview.nextPage",
		defaultMessage: "Next page",
	},
	addSiteButton: {
		id: "home.sitesOverview.addSiteButton",
		defaultMessage: "Add a site",
	},
	noSites: {
		id: "home.sitesOverview.noSites",
		defaultMessage: "Add your first site to your MyYoast account!",
	},
	noResults: {
		id: "home.sitesOverview.noResults",
		defaultMessage: "We could not find any sites matching {query}.",
	},
	searchResults: {
		id: "home.sitesOverview.searchResults",
		defaultMessage: "Number of sites found: {count}",
	},
} );

const debouncedSpeak = _debounce( speak, 1000 );

/**
 * A dismissible info alert pointing out unused subscription slots.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The rendered alert.
 */
const UnusedSubscriptionsAlert = ( { count, onAddSite, onDismiss, intl } ) => {
	return (
		<Alert as="div" variant="info" role="status">
			<div className={ styles.alertBody }>
				<span>
					<FormattedMessage
						{ ...messages.unusedActivations }
						values={ {
							count,
							link: (
								<Link as="button" type="button" onClick={ onAddSite }>
									<FormattedMessage { ...messages.addASiteLink } />
								</Link>
							),
						} }
					/>
				</span>
				<button
					type="button"
					className={ styles.alertDismissButton }
					aria-label={ intl.formatMessage( messages.dismissAlert ) }
					onClick={ onDismiss }
				>
					<XMarkIcon className={ styles.alertDismissIcon } />
				</button>
			</div>
		</Alert>
	);
};

UnusedSubscriptionsAlert.propTypes = {
	count: PropTypes.number.isRequired,
	onAddSite: PropTypes.func.isRequired,
	onDismiss: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

/**
 * A single site row in the sites overview table.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The rendered table row.
 */
const SiteRow = ( { site, rowNumber, onNavigateToSite, intl } ) => {
	const onActionClick = () => onNavigateToSite( site.id );

	// On desktop this renders as a plain table row; below a narrow card width the CSS
	// reflows the same cells into a stacked list item with each column title above its
	// value (see SitesOverviewCardStyles.module.scss). The data-label / cell classNames
	// are the reflow's hooks.
	return (
		<Table.Row className={ styles.siteRow }>
			<Table.Cell className={ styles.siteNameHeadingCell }>
				<div className={ styles.siteNameCell }>
					<span className={ styles.rowNumber }>{ rowNumber }.</span>
					<span className={ styles.siteName } title={ site.siteName }>{ site.siteName }</span>
				</div>
			</Table.Cell>
			<Table.Cell
				className={ classNames( styles.statusCell, { [ styles.inactive ]: ! site.isActive } ) }
				data-label={ intl.formatMessage( messages.headerStatus ) }
			>
				{ intl.formatMessage( site.isActive ? messages.statusActive : messages.statusInactive ) }
			</Table.Cell>
			<Table.Cell className={ styles.endsOnCell } data-label={ intl.formatMessage( messages.headerEndsOn ) }>
				{ site.endsOn ? formatDayMonthYear( site.endsOn ) : "-" }
			</Table.Cell>
			<Table.Cell className={ styles.actionsCell }>
				<Button size="small" variant="tertiary" onClick={ onActionClick }>
					{ intl.formatMessage( site.isActive ? messages.manage : messages.fix ) }
				</Button>
			</Table.Cell>
		</Table.Row>
	);
};

SiteRow.propTypes = {
	site: PropTypes.object.isRequired,
	rowNumber: PropTypes.number.isRequired,
	onNavigateToSite: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

/**
 * A card giving an overview of the user's sites, their subscription status and expiry.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The rendered sites overview card.
 */
const SitesOverviewCard = ( props ) => {
	// Counts slots on provisioned subscriptions too, where the provisioner allows site changes here —
	// so this number can be higher than the one the old selector produced, and now agrees with the
	// site page.
	const { summary } = useSubscriptionSummary();
	const unusedSlotsCount = summary.assignableUnusedSlotsCount;

	const [ query, setQuery ] = useState( "" );
	const [ page, setPage ] = useState( 1 );
	const [ alertDismissed, setAlertDismissed ] = useState( isAlertDismissed );

	useEffect( () => {
		props.loadSites();
	}, [] );

	const filteredSites = useMemo( () => {
		const rows = props.sites.map( site => ( {
			...site,
			...getSiteSubscriptionInfo( site ),
		} ) );

		if ( query.length === 0 ) {
			return rows;
		}

		const lowerCaseQuery = query.toLowerCase();
		return rows.filter( site =>
			site.siteName.toLowerCase().includes( lowerCaseQuery ) ||
			site.url.toLowerCase().includes( lowerCaseQuery ) );
	}, [ props.sites, query ] );

	const totalPages  = Math.max( 1, Math.ceil( filteredSites.length / PAGE_SIZE ) );
	const currentPage = Math.min( page, totalPages );
	const startIndex  = ( currentPage - 1 ) * PAGE_SIZE;
	const pageSites   = filteredSites.slice( startIndex, startIndex + PAGE_SIZE );

	useEffect( () => {
		if ( query.length > 0 ) {
			// Announce the number of search results to assistive technologies while typing.
			const message = props.intl.formatMessage( messages.searchResults, { count: filteredSites.length } );
			debouncedSpeak( message, "assertive" );
		}
		return () => debouncedSpeak.cancel();
	}, [ query ] );

	/**
	 * Handles a change of the search query.
	 *
	 * @param {string} newQuery The new search query.
	 *
	 * @returns {void}
	 */
	const onSearchChange = ( newQuery ) => {
		setQuery( newQuery );
		setPage( 1 );
	};

	const hasSites = props.sites.length > 0;

	return (
		<Card>
			<div className={ styles.sitesCardHeader }>
				<Title as="h2" size="4">
					<FormattedMessage { ...messages.cardTitle } />
				</Title>
			</div>
			<Card.Content>
				<div className={ styles.cardContent }>
					{ unusedSlotsCount > 0 && ! alertDismissed &&
						<UnusedSubscriptionsAlert
							count={ unusedSlotsCount }
							onAddSite={ props.onAddSite }
							onDismiss={ () => {
								dismissAlert();
								setAlertDismissed( true );
							} }
							intl={ props.intl }
						/>
					}
					{ ! hasSites &&
						<p className={ styles.noSites }>
							<FormattedMessage { ...messages.noSites } />
						</p>
					}
					{ hasSites && <>
						<Search
							id="sites-overview-search"
							descriptionId="sites-overview-search-description"
							searchLabel={ props.intl.formatMessage( messages.searchLabel ) }
							query={ query }
							onChange={ onSearchChange }
						/>
						<div className={ styles.tableWrapper }>
							<Table>
								<Table.Head>
									<Table.Row>
										<Table.Header>
											{ props.intl.formatMessage( messages.headerSiteUrl ) }
										</Table.Header>
										<Table.Header className={ styles.colStatus }>
											{ props.intl.formatMessage( messages.headerStatus ) }
										</Table.Header>
										<Table.Header className={ styles.colEndsOn }>
											{ props.intl.formatMessage( messages.headerEndsOn ) }
										</Table.Header>
										<Table.Header className={ styles.actionsCell }>
											{ props.intl.formatMessage( messages.headerActions ) }
										</Table.Header>
									</Table.Row>
								</Table.Head>
								<Table.Body>
									{ pageSites.map( ( site, index ) => (
										<SiteRow
											key={ site.id }
											site={ site }
											rowNumber={ startIndex + index + 1 }
											onNavigateToSite={ props.onNavigateToSite }
											intl={ props.intl }
										/>
									) ) }
									{ filteredSites.length === 0 &&
										<Table.Row>
											<Table.Cell className={ styles.noResults } colSpan={ 4 }>
												<FormattedMessage
													{ ...messages.noResults }
													values={ { query: <strong>{ query }</strong> } }
												/>
											</Table.Cell>
										</Table.Row>
									}
								</Table.Body>
							</Table>
						</div>
						{ filteredSites.length > 0 &&
							<div className={ styles.paginationFooter }>
								<span className={ styles.showingResults }>
									<FormattedMessage
										{ ...messages.showingResults }
										values={ {
											from: startIndex + 1,
											to: startIndex + pageSites.length,
											total: filteredSites.length,
										} }
									/>
								</span>
								{ totalPages > 1 &&
									<Pagination
										variant="text"
										current={ currentPage }
										total={ totalPages }
										onNavigate={ setPage }
										screenReaderTextPrevious={ props.intl.formatMessage( messages.previousPage ) }
										screenReaderTextNext={ props.intl.formatMessage( messages.nextPage ) }
									/>
								}
							</div>
						}
					</> }
					<Button
						id="sites-overview-add-site"
						onClick={ props.onAddSite }
						aria-label={ props.intl.formatMessage( messages.addSiteButton ) }
						className={ styles.addSiteButton }
					>
						<PlusSmallIcon className={ styles.addSiteButtonIcon } />
						<FormattedMessage { ...messages.addSiteButton } />
					</Button>
				</div>
				<AddSiteModal>
					<AddSite />
				</AddSiteModal>
			</Card.Content>
		</Card>
	);
};

SitesOverviewCard.propTypes = {
	intl: intlShape.isRequired,
	sites: PropTypes.array.isRequired,
	loadSites: PropTypes.func.isRequired,
	onAddSite: PropTypes.func.isRequired,
	onNavigateToSite: PropTypes.func.isRequired,
};

export const mapStateToProps = ( state ) => {
	return {
		sites: getViewableSites( state ),
	};
};

export const mapDispatchToProps = ( dispatch ) => {
	return {
		loadSites: () => {
			dispatch( retrieveSites() );
		},
		onAddSite: () => {
			dispatch( linkSiteModalOpen() );
		},
		onNavigateToSite: ( siteId ) => {
			dispatch( push( "/sites/" + siteId ) );
		},
	};
};

const SitesOverviewCardContainer = connect(
	mapStateToProps,
	mapDispatchToProps,
)( SitesOverviewCard );

export default injectIntl( SitesOverviewCardContainer );
